import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SiteNav } from './SiteNav';
import { Check, Search, Ruler, Calendar, Mail } from 'lucide-react';
import axios from 'axios';
import { QSPriceBadge, QS_CHECK_FEATURES, useQSPricing } from './QSPriceBadge';
import { QSCallCTA } from './QSCallCTA';
import './ScopeSuccess.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ContractorQuoteLogger — optional field on the Success page.
 * Collects real contractor quote data from beta testers so the rate engine
 * can be calibrated against market reality. Single amount + optional trade
 * tag + optional notes. Latest submission wins (non-destructive).
 */
const ContractorQuoteLogger = ({ projectId, initialAmount, initialTrade }) => {
  const [amount, setAmount] = useState(initialAmount ? String(initialAmount) : '');
  const [trade, setTrade] = useState(initialTrade || 'overall');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState(initialAmount ? 'saved' : 'idle');
  // idle | saving | saved | error

  const submit = async (e) => {
    e.preventDefault();
    const n = parseFloat(amount);
    if (!n || n <= 0) return;
    setStatus('saving');
    try {
      await axios.post(`${API}/client/projects/${projectId}/contractor-quote`, {
        amount_gbp: n,
        trade,
        notes: notes.slice(0, 500),
      });
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      data-testid="contractor-quote-logger"
      style={{
        marginTop: 18, padding: '14px 16px', borderRadius: 10,
        background: '#FEF9E7', border: '1px solid #FCD34D',
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 800, color: '#92400E', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 4 }}>
        Beta — help us calibrate
      </div>
      <div style={{ fontSize: 13, color: '#0F2742', marginBottom: 10, lineHeight: 1.5 }}>
        Already have a contractor quote for this project? Share it — we compare it against our estimate and tune the engine. <strong>Completely optional</strong>, 10 seconds, makes the tool better for everyone.
      </div>
      {status === 'saved' ? (
        <div data-testid="contractor-quote-saved" style={{ fontSize: 13, fontWeight: 700, color: '#065F46' }}>
          ✓ Thanks — saved £{parseFloat(amount).toLocaleString()} against this project.
          <button
            type="button"
            onClick={() => setStatus('idle')}
            style={{ marginLeft: 10, background: 'transparent', border: 'none', color: '#065F46', fontSize: 11, textDecoration: 'underline', cursor: 'pointer' }}
          >
            Edit
          </button>
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '0 0 160px' }}>
            <label style={{ fontSize: 11, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 3 }}>Quote amount (£)</label>
            <input
              type="number"
              inputMode="decimal"
              min="1"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 13000"
              data-testid="contractor-quote-amount"
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #CBD5E1', borderRadius: 6 }}
            />
          </div>
          <div style={{ flex: '0 0 150px' }}>
            <label style={{ fontSize: 11, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 3 }}>Covers</label>
            <select
              value={trade}
              onChange={(e) => setTrade(e.target.value)}
              data-testid="contractor-quote-trade"
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #CBD5E1', borderRadius: 6, background: '#fff' }}
            >
              <option value="overall">Whole project</option>
              <option value="kitchen">Kitchen only</option>
              <option value="bathroom">Bathroom only</option>
              <option value="structural">Structural / demolition</option>
              <option value="electrics">Electrics</option>
              <option value="plumbing">Plumbing / heating</option>
              <option value="finishes">Finishes / decorating</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <label style={{ fontSize: 11, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 3 }}>Notes (optional)</label>
            <input
              type="text"
              maxLength={500}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Local contractor, includes VAT"
              data-testid="contractor-quote-notes"
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #CBD5E1', borderRadius: 6 }}
            />
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <button
              type="submit"
              disabled={status === 'saving' || !amount}
              data-testid="contractor-quote-submit"
              style={{
                background: '#0F2742', color: '#fff', border: 'none',
                padding: '9px 16px', borderRadius: 6, fontSize: 13, fontWeight: 700,
                cursor: (status === 'saving' || !amount) ? 'not-allowed' : 'pointer',
                opacity: (status === 'saving' || !amount) ? 0.6 : 1,
              }}
            >
              {status === 'saving' ? 'Saving…' : 'Share'}
            </button>
          </div>
          {status === 'error' && (
            <div style={{ flex: '1 1 100%', fontSize: 11, color: '#B91C1C', marginTop: 4 }}>
              Couldn't save — please try again.
            </div>
          )}
        </form>
      )}
    </div>
  );
};

const TITLES = {
  indicative: 'Your cost guide is ready',
  detailed: 'Your Full Project Pack is ready',
  scope_full: 'Your Full Project Pack is ready',
  scope_sow: 'Your Schedule of Works is ready',
  scope_materials: 'Your Materials Schedule is ready',
  'qs-check': 'QS Check confirmed',
};
const SUBS = {
  indicative: 'Your cost breakdown, materials list and project guide are ready to download below.',
  detailed: 'Your Schedule of Works, Cost Estimate, Materials Schedule and contractor guide are ready to download.',
  scope_full: 'Your Schedule of Works, Cost Estimate, Materials Schedule and contractor guide are ready to download.',
  scope_sow: 'Your per-room Schedule of Works is ready to download.',
  scope_materials: 'Your Materials Schedule is ready to download.',
  'qs-check': 'One of our Quantity Surveyors will review your estimate and deliver a QS-verified report within 2 working days.',
};

// Documents included per product type (mirrors backend TIER_ALLOWED_REPORTS)
const DOCS_BY_PRODUCT = {
  indicative: [
    { icon: '📋', name: 'Cost Estimate', desc: 'Trade-by-trade cost guide for your project', type: 'breakdown' },
    { icon: '🪵', name: 'Materials List', desc: 'Key materials before buying or getting quotes', type: 'materials' },
    { icon: '⚡', name: 'What To Do Next', desc: 'Questions to ask contractors and project plan', type: 'guide' },
  ],
  detailed: [
    { icon: '📐', name: 'Schedule of Works', desc: 'Per-room, trade-by-trade scope', type: 'sow' },
    { icon: '💰', name: 'Cost Estimate', desc: 'Trade-by-trade costs', type: 'breakdown' },
    { icon: '🪵', name: 'Materials Schedule', desc: 'Key materials for procurement and supplier quotes', type: 'materials' },
    { icon: '⚡', name: 'What To Do Next', desc: 'Questions to ask contractors and how to compare quotes', type: 'guide' },
  ],
  scope_full: [
    { icon: '📐', name: 'Schedule of Works (PDF)', desc: 'Per-room, trade-by-trade scope', type: 'sow' },
    { icon: '📊', name: 'Schedule of Works — Tender (Excel)', desc: 'Priceable workbook — send to contractors for fixed-price quotes', type: 'sow_xlsx' },
    { icon: '💰', name: 'Cost Estimate', desc: 'Trade-by-trade costs', type: 'breakdown' },
    { icon: '🪵', name: 'Materials Schedule', desc: 'Key materials for procurement and supplier quotes', type: 'materials' },
    { icon: '⚡', name: 'What To Do Next', desc: 'Questions to ask contractors and how to compare quotes', type: 'guide' },
  ],
  scope_sow: [
    { icon: '📐', name: 'Schedule of Works (PDF)', desc: 'Per-room, trade-by-trade scope', type: 'sow' },
    { icon: '📊', name: 'Schedule of Works — Tender (Excel)', desc: 'Priceable workbook — send to contractors for fixed-price quotes', type: 'sow_xlsx' },
  ],
  scope_materials: [
    { icon: '🪵', name: 'Materials Schedule', desc: 'Key materials for procurement and supplier quotes', type: 'materials' },
  ],
};

export const ScopeSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const product = searchParams.get('product') || 'indicative';
  const sessionId = searchParams.get('session_id') || '';
  const orderRef = searchParams.get('ref') || searchParams.get('order_ref') || '';
  const email = searchParams.get('email') || '';
  const address = searchParams.get('address') || 'Your property';
  const isFree = searchParams.get('free') === 'true';

  const [projectData, setProjectData] = useState(null);
  const [consentSaved, setConsentSaved] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [userTier, setUserTier] = useState(null);  // null | 'free' | 'pro' | 'pro_plus'

  // Fetch the buyer's tier so the expiry notice is accurate
  useEffect(() => {
    let stopped = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/billing/me`);
        if (!stopped) setUserTier(res.data?.tier || 'free');
      } catch {
        if (!stopped) setUserTier('free');  // not logged in or no sub → free
      }
    })();
    return () => { stopped = true; };
  }, []);

  const isSubscriber = userTier === 'pro' || userTier === 'pro_plus';
  const expiryCopyShort = isSubscriber
    ? 'Documents available for the duration of your subscription.'
    : 'Documents available for 180 days from today.';
  const expiryCopyTitle = isSubscriber
    ? 'Your documents are saved for the duration of your subscription'
    : 'Your documents are saved for 180 days';

  // Fetch project data if we have a session_id
  useEffect(() => {
    if (sessionId) {
      let stopped = false;
      const fetchData = async () => {
        try {
          const res = await axios.get(`${API}/scope/confirmation?session_id=${sessionId}`);
          if (res.data && res.data.status === 'paid') {
            setProjectData(res.data);
            stopped = true;
          }
        } catch (err) { console.debug('Polling for confirmation...', err?.message); }
      };
      fetchData();
      const interval = setInterval(() => { if (!stopped) fetchData(); else clearInterval(interval); }, 3000);
      setTimeout(() => clearInterval(interval), 60000);
      return () => clearInterval(interval);
    }
  }, [sessionId]);

  const displayRef = projectData?.order_ref || orderRef || 'SOW26-000000';
  const displayEmail = projectData?.client_email || email || 'your email address';
  const displayAddress = projectData?.address || decodeURIComponent(address);

  // Trigger a click-to-download via a hidden anchor. Bypasses pop-up blockers
  // that fire when using window.open('_blank') on a signed URL (§5.4 Apr 2026 spec).
  const triggerDownload = (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || '';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    // slight delay so browsers register the click before removing the node
    setTimeout(() => { try { document.body.removeChild(a); } catch (_) { /* noop */ } }, 100);
  };

  const handleDownload = async (type) => {
    try {
      const res = await axios.get(`${API}/scope/download?ref=${encodeURIComponent(displayRef)}&type=${type}`);
      if (res.data.url) {
        const ext = type === 'sow_xlsx' ? 'xlsx' : 'pdf';
        triggerDownload(res.data.url, `${type}.${ext}`);
      } else {
        alert('Download not available yet. Please try again in a moment.');
      }
    } catch (err) {
      console.error('Download failed:', err?.message);
      alert('Download not available yet. Your documents may still be generating.');
    }
  };

  const handleDownloadAll = async () => {
    const docs = DOCS_BY_PRODUCT[product] || DOCS_BY_PRODUCT.indicative;
    for (const doc of docs) {
      try {
        const res = await axios.get(`${API}/scope/download?ref=${encodeURIComponent(displayRef)}&type=${doc.type}`);
        if (res.data.url) {
          const ext = doc.type === 'sow_xlsx' ? 'xlsx' : 'pdf';
          triggerDownload(res.data.url, `${doc.type}.${ext}`);
          await new Promise(r => setTimeout(r, 500));
        }
      } catch (err) { console.error('Download batch skip:', err?.message); }
    }
  };

  const handleAddQSCheck = async () => {
    try {
      const res = await axios.post(`${API}/scope/checkout`, {
        product_type: 'qs_check',
        email: displayEmail,
        address: displayAddress,
        linked_ref: displayRef,
      });
      window.location.href = res.data.url;
    } catch (err) {
      console.error('QS Check checkout error:', err?.message);
      alert('Unable to start QS Check checkout. Please try again.');
    }
  };

  const handleSaveConsent = async () => {
    try {
      await axios.post(`${API}/scope/consent`, {
        ref: displayRef,
        email: displayEmail,
        consented: consentChecked,
      });
    } catch (err) { console.error('Consent save error:', err?.message); }
    setConsentSaved(true);
  };

  return (
    <div className="ss-app" data-testid="scope-success-page">
      <SiteNav cta="home" />

      <div className="ss-main">
        {/* Success Header */}
        <div className="ss-hdr" data-testid="success-header">
          <div className="ss-icon">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <div className="ss-title">{TITLES[product] || TITLES.indicative}</div>
          <div className="ss-sub">{SUBS[product] || SUBS.indicative}</div>
          <div className="ss-ref">Order reference: <span>{displayRef}</span></div>
        </div>

        <div className="ss-cols">
          {/* LEFT COLUMN */}
          <div>
            {/* SCOPE DOWNLOADS — data-driven per product */}
            {(['indicative', 'detailed', 'scope_full', 'scope_sow', 'scope_materials'].includes(product)) && (
              <div className="ss-dl-card" data-testid={`section-${product}`}>
                <div className="ss-dl-hdr">
                  <div className="ss-dl-hdr-label">{TITLES[product] || TITLES.indicative}</div>
                  <div className="ss-dl-hdr-addr">{displayAddress}</div>
                </div>
                <div className="ss-dl-body">
                  <div className="ss-dl-items">
                    {(DOCS_BY_PRODUCT[product] || DOCS_BY_PRODUCT.indicative).map(item => (
                      <div key={item.type} className="ss-dl-item">
                        <div className="ss-dl-item-left">
                          <div className="ss-dl-item-icon">{item.icon}</div>
                          <div>
                            <div className="ss-dl-item-name">{item.name}</div>
                            <div className="ss-dl-item-desc">{item.desc}</div>
                          </div>
                        </div>
                        <button className="ss-btn-dl" onClick={() => handleDownload(item.type)} data-testid={`dl-${item.type}`}>
                          ↓ Download
                        </button>
                      </div>
                    ))}
                  </div>
                  {(DOCS_BY_PRODUCT[product] || []).length > 1 && (
                    <button className="ss-btn-dl-all" onClick={handleDownloadAll} data-testid="dl-all">
                      ↓ Download all {(DOCS_BY_PRODUCT[product] || []).length} documents
                    </button>
                  )}
                  <div className="ss-email-note">
                    <span>📧</span>
                    <span>A copy has been sent to <strong>{displayEmail}</strong>. {expiryCopyShort}</span>
                  </div>
                </div>
              </div>
            )}

            {/* QS CHECK CONFIRMATION */}
            {product === 'qs-check' && (
              <div className="ss-dl-card" data-testid="section-qs-check">
                <div className="ss-dl-hdr">
                  <div className="ss-dl-hdr-label">QS Check</div>
                  <div className="ss-dl-hdr-addr">Your order is confirmed</div>
                </div>
                <div className="ss-dl-body">
                  <div style={{ padding: '8px 0 16px' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ss-navy)', marginBottom: 8 }}>What happens next</div>
                    <div className="ss-next-steps">
                      {[
                        { n: 1, text: <><strong>Within 24 hours</strong> — one of our Quantity Surveyors will review your project and begin the verification process.</> },
                        { n: 2, text: <><strong>Within 2 working days</strong> — your QS-verified report will be emailed to you with figures checked, assumptions flagged and a written summary.</> },
                        { n: 3, text: <><strong>If we have any questions</strong> — we will contact you by email. Please check your inbox over the next 24 hours.</> },
                      ].map(s => (
                        <div key={s.n} className="ss-next-step">
                          <div className="ss-next-num">{s.n}</div>
                          <div className="ss-next-text">{s.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="ss-email-note">
                    <span>📧</span>
                    <span>A confirmation has been sent to <strong>{displayEmail}</strong> with your order reference.</span>
                  </div>
                </div>
              </div>
            )}

            {/* MARKETING CONSENT */}
            <div className="ss-consent" data-testid="consent-card">
              {!consentSaved ? (
                <>
                  <div className="ss-consent-title">Stay in the loop</div>
                  <div className="ss-consent-desc">Help me get the best value on my project — send me contractor rate insights, cost-saving tips and market updates relevant to my refurbishment.</div>
                  <div className="ss-consent-check">
                    <input type="checkbox" id="marketing-consent" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />
                    <label htmlFor="marketing-consent">Help me get the best value on my project — send me contractor rate insights, cost-saving tips and market updates relevant to my refurbishment</label>
                  </div>
                  <button className="ss-btn-consent" onClick={handleSaveConsent} data-testid="save-consent-btn">Save preference</button>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '8px 0', fontSize: 13, fontWeight: 700, color: '#16A34A' }}>
                  <Check className="w-4 h-4 inline mr-1" /> Preference saved
                </div>
              )}
            </div>

            {/* WHAT'S NEXT */}
            {(product === 'indicative' || product === 'detailed') && (
              <div className="ss-whats-next" data-testid="whats-next">
                <div className="ss-wn-title">Making the most of your documents</div>
                <div className="ss-next-steps">
                  {[
                    { n: 1, text: <><strong>Read the cost breakdown first</strong> — understand what each trade costs before speaking to anyone.</> },
                    { n: 2, text: <><strong>Use the materials list</strong> — share with suppliers before getting quotes to understand material costs independently.</> },
                    { n: 3, text: <><strong>Brief at least three contractors</strong> — use the questions in your guide. Comparing quotes against your cost breakdown tells you who is pricing fairly.</> },
                    { n: 4, text: <><strong>Something doesn't add up?</strong> — add a QS Check and one of our Quantity Surveyors will verify your figures before you commit.</> },
                  ].map(s => (
                    <div key={s.n} className="ss-next-step">
                      <div className="ss-next-num">{s.n}</div>
                      <div className="ss-next-text">{s.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT SIDEBAR — UPSELLS */}
          <div>
            {/* QS CHECK upsell */}
            {(product === 'indicative' || product === 'detailed') && (
              <div data-testid="upsell-qs-check">
                <div className="ss-sidebar-label">Want to go further?</div>
                <div className="ss-upsell-card qs-check">
                  <div className="ss-upsell-hdr">
                    <div className="ss-upsell-icon"><Search className="w-4 h-4 text-white" /></div>
                    <div>
                      <div className="ss-upsell-title">Want total confidence in your numbers?</div>
                      <div className="ss-upsell-desc">A qualified Quantity Surveyor reviews your estimate — quantities and rates checked, assumptions and exclusions flagged, with contractor guidance on how to find, vet and appoint the right contractor. Delivered in 2 working days.</div>
                    </div>
                  </div>
                  <QSPriceBadge variant="large" className="ss-upsell-price" />
                  <div className="ss-upsell-features">
                    {QS_CHECK_FEATURES.map(f => (
                      <div key={f} className="ss-upsell-feature">
                        <span className="ss-upsell-tick"><Check className="w-3 h-3" /></span>
                        {f}
                      </div>
                    ))}
                  </div>
                  <button className="ss-btn-upsell" onClick={handleAddQSCheck} data-testid="upsell-qs-btn">
                    Add QS Check — <QSPriceBadge variant="button" /> →
                  </button>
                </div>

                {/* QS Consultation Call — £199 (Phase D) */}
                <div style={{ marginTop: 16 }} data-testid="upsell-qs-call-wrap">
                  <QSCallCTA variant="card" showRedeem testid="qs-call-cta-success" />
                </div>
              </div>
            )}

            {/* DETAILED upsell (indicative only) */}
            {product === 'indicative' && (
              <div data-testid="upsell-detailed">
                <div className="ss-upsell-card">
                  <div className="ss-upsell-hdr">
                    <div className="ss-upsell-icon"><Ruler className="w-4 h-4 text-white" /></div>
                    <div>
                      <div className="ss-upsell-title">Need precise quantities for tender?</div>
                      <div className="ss-upsell-desc">Upgrade to a full measured Schedule of Works — upload your floor plan, measure room by room and get a QS-grade document you can take to contractors.</div>
                    </div>
                  </div>
                  <div className="ss-upsell-price">£199</div>
                  <div className="ss-upsell-features">
                    {['Full measured Schedule of Works', 'Cost Estimate with regional adjustment', 'Key Materials Schedule', 'What To Do Next contractor guide'].map(f => (
                      <div key={f} className="ss-upsell-feature">
                        <span className="ss-upsell-tick"><Check className="w-3 h-3" /></span>
                        {f}
                      </div>
                    ))}
                  </div>
                  <button className="ss-btn-upsell navy" onClick={() => navigate('/scope')} data-testid="upsell-detailed-btn">
                    Upgrade to Detailed — £199 →
                  </button>
                </div>
              </div>
            )}

            {/* Contractor quote calibration logger (optional, beta data) */}
            {!isFree && (projectData?.project_id) && (
              <ContractorQuoteLogger
                projectId={projectData.project_id}
                initialAmount={projectData.contractor_quote?.amount_gbp}
                initialTrade={projectData.contractor_quote?.trade}
              />
            )}

            {/* Project expiry */}
            <div className="ss-expiry" data-testid="expiry-reminder">
              <div className="ss-expiry-title"><Calendar className="w-3.5 h-3.5 inline" /> {expiryCopyTitle}</div>
              <div className="ss-expiry-desc">We will email you a reminder before your documents expire. You can also re-download from the link in your confirmation email at any time.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="ss-footer">
        <div className="ss-footer-inner">
          <div className="ss-footer-brand">SCHEDULES OF WORK · RESIDENTIAL REFURB</div>
          <div className="ss-footer-links">
            <button onClick={() => navigate('/terms')}>Terms</button>
            <button onClick={() => navigate('/terms')}>Privacy</button>
            <button onClick={() => navigate('/faq')}>FAQ</button>
          </div>
        </div>
      </footer>
    </div>
  );
};
